import { BLOCKS } from '@contentful/rich-text-types';
import type { Block, Document, Inline } from '@contentful/rich-text-types';
import type { Faq, RichText } from '@silvertours/common-landingpages-view';

export const FAQ_ITEM_CONTENT_TYPE = 'faqItem';

type EmbeddedEntryTarget = {
  sys?: { contentType?: { sys?: { id?: string } } };
  fields?: { summary?: string; details?: Document; headingLevel?: string };
};

const targetOf = (node: Block | Inline) =>
  (node.data as { target?: EmbeddedEntryTarget }).target;

export const isFaqItemEmbed = (node: Block | Inline) =>
  node.nodeType === BLOCKS.EMBEDDED_ENTRY &&
  targetOf(node)?.sys?.contentType?.sys?.id === FAQ_ITEM_CONTENT_TYPE;

/**
 * `faqItem.summary`/`faqItem.details` are optional in Contentful; the
 * dedicated FAQ module's backend mapping drops items missing either field
 * (see back-data-contentful Faq.ts), so the rich-text embed path mirrors
 * that here since it bypasses backend domain mapping entirely.
 */
export const extractFaqItemFromTarget = (target: unknown): Faq | undefined => {
  const fields = (target as EmbeddedEntryTarget | undefined)?.fields;
  const question = fields?.summary;
  const answer = fields?.details;

  if (!question || !answer) return undefined;

  return { question, answer: { type: 'contentful', text: answer } };
};

/**
 * Only the rich-text embed uses `faqItem.headingLevel`, so it is read here and
 * not added to the `Faq` view type that the FAQ module and structured data
 * share. Any value other than `h2` shows the default h3.
 */
export const extractFaqHeadingLevelFromTarget = (
  target: unknown,
): 'h2' | 'h3' =>
  (target as EmbeddedEntryTarget | undefined)?.fields?.headingLevel === 'h2'
    ? 'h2'
    : 'h3';

export const extractEmbeddedFaqItems = (content?: RichText): Faq[] => {
  if (content?.type !== 'contentful') return [];

  return (content.text.content as Array<Block | Inline>)
    .filter(isFaqItemEmbed)
    .map(node => extractFaqItemFromTarget(targetOf(node)))
    .filter((faq): faq is Faq => !!faq);
};
